import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/system';
import { format } from 'date-fns';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useTranslation, useTableSort } from '@hooks';
import { analyticsService } from '@services';
import ExportCSV from '@features/Analytics/ExportCSV';
import { splitSurveyGroups } from '@helpers/surveyFilters';
import { getTableBody } from './GetTableBody';
import { getCollapsibleTableBody } from './CollapsibleRowBody';

const SORT_COLUMNS = {
    location: 'internalName',
    sentSurveys: 'sentSurveys',
    openedSurveys: 'openedSurveys',
    responsesReceived: 'responsesReceived',
    leaveReviewClicked: 'leaveReviewClicked',
    reviewsReceived: 'reviewsReceived',
};

const DEFAULT_SORT = { column: 'location', dir: 'asc' };

const GROUP_BY = { key: row => row.id, isParent: row => !row.surveyTemplateId };

const OVERALL_ROW_KEY = 'overall';

const isOverallRow = row => row.id === null && row.surveyTemplateId === null;

export const PaperStyled = styled(Paper)({
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    borderRadius: '8px',
    '&:not(:last-child)': {
        marginBottom: 16,
    },
});

export const TableStyled = styled(Table)({
    '& tbody .MuiTableRow-root[data-row=true]': {
        backgroundColor: 'rgba(247, 251, 249)',
    },
});

export const TableCellText = styled(Typography)({
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
});

export const Text = styled('span', { shouldForwardProp: prop => prop !== 'withIcon' })(({ withIcon }) => ({
    width: 'auto',
    marginLeft: '4px',
    color: 'var(--text-secondary)',
    marginRight: '28px',
    ...(withIcon && {
        marginRight: '8px',
    }),
}));

const getTableHeader = (t, isQr) => {
    const width = isQr ? '18%' : '14%';
    return [
        {
            key: 0,
            component: <></>,
            width: '5%',
        },
        {
            key: 1,
            sortKey: 'location',
            align: 'left',
            component: (
                <Typography variant="subtitle2" align="left" sx={{ minWidth: '100px' }}>
                    {t('common.location')}
                </Typography>
            ),
            width: '100px',
        },
        {
            key: 2,
            sortKey: isQr ? 'openedSurveys' : 'sentSurveys',
            component: (
                <Typography variant="subtitle2" align="center" sx={{ minWidth: '89px' }}>
                    {isQr ? t('AnalyticsConversion.openedSurveys') : t('AnalyticsConversion.sentSurveys')}
                </Typography>
            ),
            width,
        },
        ...(!isQr
            ? [
                  {
                      key: 3,
                      sortKey: 'openedSurveys',
                      component: (
                          <Typography align="center" variant="subtitle2" sx={{ minWidth: '89px' }}>
                              {t('AnalyticsConversion.openedSurveys')}
                          </Typography>
                      ),
                      width,
                  },
              ]
            : []),
        {
            key: 4,
            sortKey: 'responsesReceived',
            component: (
                <Typography align="center" variant="subtitle2" sx={{ minWidth: '89px' }}>
                    {t('AnalyticsConversion.responsesReceived')}
                </Typography>
            ),
            width,
        },
        {
            key: 5,
            sortKey: 'leaveReviewClicked',
            component: (
                <Typography align="center" variant="subtitle2" sx={{ minWidth: '89px' }}>
                    {t('AnalyticsConversion.proceededToReviewSite')}
                </Typography>
            ),
            width,
        },
        {
            key: 6,
            sortKey: 'reviewsReceived',
            component: (
                <Typography align="center" variant="subtitle2" sx={{ minWidth: '89px' }}>
                    {t('AnalyticsConversion.reviewsReceived')}
                </Typography>
            ),
            width,
        },
    ];
};

export const getTableArrow = field => {
    if (field > 0) {
        return <ArrowUpwardIcon sx={{ color: 'var(--success-main)', fontSize: '20px !important' }} />;
    } else if (field < 0) {
        return <ArrowDownwardIcon sx={{ color: 'var(--error-main)', fontSize: '20px !important' }} />;
    }
    return null;
};

const CollapsibleRow = ({ data, isQr, index }) => {
    return <Table rows={getCollapsibleTableBody(data, index, isQr)} />;
};

const ConversionTable = ({ isLoading, data, filter, type }) => {
    const locations = useSelector(state => state.account?.account?.companies || []);
    const surveys = useSelector(state => state.surveys.data);
    const { t } = useTranslation();

    const onExport = (exportType, exportFormat) =>
        analyticsService.exportAnalyticsCSV(
            {
                locations: filter.locations.map(item => item.id),
                surveys: splitSurveyGroups(filter.surveys).surveys,
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            'conversion',
            exportType,
            exportFormat,
        );

    const isQr = type === 'qr';

    const dataWithNames = useMemo(
        () =>
            data.map(item => ({
                ...item,
                internalName: locations.find(location => location.companyId === item.id)?.internalName || '',
                surveyName: surveys.find(s => s.survey.id === item.surveyTemplateId)?.survey?.name || '',
            })),
        [data, locations, surveys],
    );

    const { sort, onSortChange, sortedData } = useTableSort(dataWithNames, {
        columns: SORT_COLUMNS,
        defaultSort: DEFAULT_SORT,
        groupBy: GROUP_BY,
        pinnedLast: isOverallRow,
    });

    return (
        <PaperStyled elevation={1}>
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t(`AnalyticsConversion.${isQr ? 'qrCodes' : 'surveyInvitations'}`)}
                </Typography>
                <ExportCSV
                    type={isQr ? 'qr' : 'surveys'}
                    apiCall={exportFormat => onExport(isQr ? 'qr' : 'surveys', exportFormat)}
                />
            </div>
            <TableContainer>
                <TableStyled
                    data={sortedData}
                    columnNames={getTableHeader(t, isQr)}
                    rows={getTableBody(sortedData, isQr, t)}
                    isDataLoading={isLoading}
                    isCollapsible={true}
                    CollapsibleRow={props => <CollapsibleRow {...props} isQr={isQr} />}
                    withTotalRow
                    sort={sort}
                    onSortChange={onSortChange}
                    getRowKey={row => row.id ?? OVERALL_ROW_KEY}
                />
            </TableContainer>
        </PaperStyled>
    );
};

export default ConversionTable;
